import React from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Box } from '@chakra-ui/react';
import { AmountType, SystemTranslationKeys } from '@common/entities';
import { CartCharge } from 'state/slices/shoppingCartSlice';

import { useSystemTranslation } from 'hooks';
import {
  appendFrequency,
  formatCustomFeeAmount,
  getLeaseChargeFrequencyTranslationKey,
  translateChargeDetailText,
} from 'utils/charge-utils';
import { formatCurrency } from 'utils/format';

import FeeTransparencyPopover from './FeeTransparencyPopover';

interface Props {
  charge: CartCharge;
}

export function InstallmentChargeItem({ charge }: Props) {
  const { tSystem } = useSystemTranslation();
  const frequencyTranslationKey = getLeaseChargeFrequencyTranslationKey(charge.arTriggerId);
  const frequency = frequencyTranslationKey
    ? tSystem(frequencyTranslationKey)
    : translateChargeDetailText(tSystem, 'frequency', charge.allChargesDetails?.[0]?.frequency);
  const taxAmount = Number(charge.taxAmount?.amount ?? 0);
  const customFeeText = formatCustomFeeAmount({
    amountType: charge.customFeeAmountType,
    amountDetail: charge.amountDetail,
    frequency,
    variesByUsageLabel: tSystem(SystemTranslationKeys.cart.amountVariesByUsage),
  });

  let rate: string;
  if (customFeeText !== undefined) {
    rate = customFeeText;
  } else if (charge.amountType === AmountType.VariesAmount) {
    rate = tSystem(SystemTranslationKeys.cart.varies);
  } else if (
    charge.isRange &&
    charge.minPriceNumber !== undefined &&
    charge.maxPriceNumber !== undefined
  ) {
    rate = appendFrequency(
      `${formatCurrency(charge.minPriceNumber + taxAmount, charge.currencyCode)}–${formatCurrency(
        charge.maxPriceNumber + taxAmount,
        charge.currencyCode,
      )}`,
      frequency,
    );
  } else {
    rate = appendFrequency(
      formatCurrency(charge.priceNumber + taxAmount, charge.currencyCode),
      frequency,
    );
  }

  const details = charge.allChargesDetails;
  const firstChargeDetail = details?.[0];
  // Core identifies installment discounts as Special charges and uses the
  // installment-plan title as their origin reference. That title describes the
  // setup option, not the number of payments this applicant will make. Core uses
  // the same "Discount" name for both discounts and premiums, so use the amount
  // direction to provide the applicant-facing label.
  let { label } = charge;
  if (firstChargeDetail?.arOriginName === 'Special') {
    const installmentAmount = charge.totalValueWithTax || charge.priceNumber;
    if (installmentAmount > 0) {
      label = tSystem(SystemTranslationKeys.payment.premium);
    } else if (installmentAmount < 0) {
      label = tSystem(SystemTranslationKeys.payment.discount);
    } else {
      label = firstChargeDetail.arCodeName;
    }
  } else if ((details?.length ?? 0) > 1) {
    label = charge.arCodeName ?? charge.label;
  }
  const total = formatCurrency(charge.totalValueWithTax ?? 0, charge.currencyCode);

  return (
    <Box
      position="relative"
      display="flex"
      alignItems="baseline"
      minW={0}
      py="2px"
      lineHeight="1.5"
      fontSize="13px"
      color="gray.drakest"
    >
      <Box
        aria-hidden
        position="absolute"
        left={0}
        right={0}
        top="2px"
        h="1.15em"
        borderBottomWidth="2px"
        borderBottomStyle="dotted"
        borderColor="gray.base"
        pointerEvents="none"
      />
      <Box
        display="flex"
        alignItems="center"
        minW={0}
        flex="1"
        position="relative"
        zIndex={1}
        bg="chakra-body-bg"
        pr="6px"
      >
        <Box
          as="span"
          title={label}
          minW={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {label}
        </Box>
        {firstChargeDetail && (
          <FeeTransparencyPopover
            label={label}
            charges={[
              { ...firstChargeDetail, frequency: frequency ?? firstChargeDetail.frequency },
            ]}
          >
            <InfoOutlineIcon ml={2} />
          </FeeTransparencyPopover>
        )}
      </Box>
      <Box
        position="relative"
        zIndex={1}
        flexShrink={0}
        bg="chakra-body-bg"
        pl="6px"
        textAlign="right"
        whiteSpace="nowrap"
      >
        {rate} / {total}
      </Box>
    </Box>
  );
}
